import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../CSS/CampusSafety.css';

const CampusSafety: React.FC = () => {
  const navigate = useNavigate();
  const imagePath = `${process.env.PUBLIC_URL || ''}/assets/Web Version/Services/Strategic Campus Safety Audits/campus-safety-audits-banner-image.png`;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return (
    <div className="campus-safety-page">
      <div className="campus-safety-hero-banner">
        <div 
          className="campus-safety-hero-background"
          style={{ 
            backgroundImage: `url("${imagePath}")` 
          }}
        ></div>
        <div className="campus-safety-hero-content">
          <p className="campus-safety-breadcrumb">Our Services / Strategic Campus Safety Audits</p>
          <h1 className="campus-safety-title">Strategic Campus Safety Audits</h1>
        </div>
      </div>

      <div className="campus-safety-body">
        <div className="campus-safety-intro">
          <h2 className="campus-safety-intro-title">Building a Resilient & Clery-Compliant Institution</h2>
          <h2 className="campus-safety-intro-subtitle">Protecting Your Most Valuable Assets: Students, Faculty, and Institutional Reputation.</h2>
          <p className="campus-safety-intro-description">
            As a trusted partner in higher education, Myers McRae provides comprehensive <b>Campus Safety Audits</b> designed to transform risk into institutional resilience. We empower your college or university to meet stringent compliance standards and proactively foster an environment of safety and excellence across all aspects of campus life.
          </p>
        </div>
      </div>

      <div className="campus-safety-audit">
        <h2 className="campus-safety-audit-title">Our Systematic Audit Approach</h2>
        <p className="campus-safety-audit-description">
          Our audits go beyond mere compliance, serving as a strategic tool for continuous improvement. The process is divided into three focused phases:
        </p>
        <div className="campus-safety-audit-cards">
          <div className="campus-safety-audit-card">
            <div className="campus-safety-audit-orb-container">
              <div className="campus-safety-audit-orb">
                <span className="campus-safety-audit-orb-label">Phase</span>
                <span className="campus-safety-audit-orb-number">1</span>
              </div>
            </div>
            <div className="campus-safety-audit-card-content">
              <h3 className="campus-safety-audit-card-title">Comprehensive Compliance Review</h3>
              <p className="campus-safety-audit-card-description">
                Each audit begins with a thorough, document-based review of your existing safety programs, including occupational health, emergency preparedness, fire prevention, environmental health, laboratory safety, and campus security. We assess compliance with all federal, state, and local mandates, with a critical focus on:
              </p>
              <ul className="campus-safety-audit-card-list">
                <li><strong>The Clery Act</strong> and its related reporting and disclosure requirements.</li>
                <li><strong>OSHA</strong> regulations for occupational health and safety.</li>
                <li><strong>NFPA</strong> standards for fire and life safety.</li>
                <li><strong>Title IX</strong> implications as they relate to campus safety and incident response (if applicable).</li>
              </ul>
            </div>
          </div>

          <div className="campus-safety-audit-card">
            <div className="campus-safety-audit-orb-container">
              <div className="campus-safety-audit-orb">
                <span className="campus-safety-audit-orb-label">Phase</span>
                <span className="campus-safety-audit-orb-number">2</span>
              </div>
            </div>
            <div className="campus-safety-audit-card-content">
              <h3 className="campus-safety-audit-card-title">On-Site Facility and Operational Assessment</h3>
              <p className="campus-safety-audit-card-description">
                Our team conducts an on-site inspection of facilities—from academic buildings and laboratories to residence halls and athletic areas. We evaluate the physical and mechanical conditions of:
              </p>
              <ul className="campus-safety-audit-card-list">
                <li>Fire and Life Safety Systems</li>
                <li>Hazardous Material Handling and storage protocols.</li>
                <li>Electrical and mechanical safety.</li>
                <li>Accessibility Compliance (ADA)</li>
                <li>Emergency signage and safety equipment readiness.</li>
              </ul>
            </div>
          </div>

          <div className="campus-safety-audit-card">
            <div className="campus-safety-audit-orb-container">
              <div className="campus-safety-audit-orb">
                <span className="campus-safety-audit-orb-label">Phase</span>
                <span className="campus-safety-audit-orb-number">3</span>
              </div>
            </div>
            <div className="campus-safety-audit-card-content">
              <h3 className="campus-safety-audit-card-title">Crisis Preparedness and Safety Culture</h3>
              <p className="campus-safety-audit-card-description">
                A defining feature of our service is our evaluation of campus safety culture. Beyond response plans, we assess:
              </p>
              <ul className="campus-safety-audit-card-list">
                <li><strong>Crisis Management</strong> and communication plans, including evacuation and coordination with local emergency services.</li>
                <li>Effectiveness of safety training programs (e.g., fire drills, active threat preparedness).</li>
                <li><strong>Safety Culture Measurement</strong>: Through surveys and interviews, we measure awareness, attitudes, and engagement across faculty, staff, and students, helping leaders understand how safety is truly practiced at every level.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="campus-safety-deliverables">
        <div className="campus-safety-deliverables-container">
          <div className="campus-safety-deliverables-text">
            <h2 className="campus-safety-deliverables-title">Deliverables and Outcomes</h2>
            <p className="campus-safety-deliverables-description">
              Following the audit, clients receive a comprehensive report that provides clear direction and actionable strategies, including:
            </p>
            <ul className="campus-safety-deliverables-list">
              <li><b>Key Findings</b> and compliance gaps.</li>
              <li><b>Prioritized Corrective Actions</b> for immediate and intermediate risks.</li>
              <li>Short- and long-term strategies for sustained improvement.</li>
              <li>Performance indicators for continuous monitoring and accountability.</li>
            </ul>
          </div>
          <div className="campus-safety-deliverables-image">
            <img 
              src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Services/AI Innovation & Academic Modernization/deliverables-and-outcomes-section-image.png`}
              alt="Deliverables and Outcomes"
            />
          </div>
        </div>
      </div>

      <div className="campus-safety-why">
        <div className="campus-safety-why-container">
          <div className="campus-safety-why-title-section">
            <h2 className="campus-safety-why-title">
              Why Partner with {!isMobile && <br />}Myers McRae
            </h2>
          </div>
          <div className="campus-safety-why-content">
            <p className="campus-safety-why-description">
              We combine deep experience in <strong>higher education leadership</strong> with an unwavering commitment to safety and integrity. By systematically identifying risks and strengthening institutional practices, we help colleges and universities:
            </p>
            <ul className="campus-safety-why-list">
              <li>Build safer, more <strong>inclusive learning environments.</strong></li>
              <li>Enhance operational efficiency and accountability.</li>
              <li>Demonstrate compliance and <strong>fiduciary stewardship</strong> to stakeholders and regulators.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="campus-safety-contact">
        <h2 className="campus-safety-contact-title">Ready to strengthen your campus safety posture?</h2>
        <button 
          className="campus-safety-contact-button"
          onClick={() => {
            navigate('/contact-us');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          Contact us today to schedule an initial consultation
        </button>
      </div>
    </div>
  );
};

export default CampusSafety;

